/* ═══════════════════════════════════════════════════════════════════════════════
   Variation Comments & @Mentions Tour (v2.5.0)
   
   Feature update tour for the enhanced comment and mention system:
   
   1. @mention team members in variation comments
      - ContentEditable rich editor with mention dropdown
      - Teal-colored mention chips with Users icon
      - Mentions load from org_members (same org)
   
   2. Color-coded comment flags (existed before, now more prominent):
      - 🟢 Green  = Resolved / Agreement Made
      - 🔴 Red    = Urgent / Requires Action  
      - 🟣 Purple = Discussion / Question
   
   3. Microsoft Teams notifications via Power Automate:
      - Edge function parses @mentions from comment text
      - Mentioned users receive direct Teams notifications
      - Different adaptive card templates per flag color
   
   4. Comms tab also supports @mentions for team members:
      - Same teal chip styling, same org member list
      - notify-project-note edge function routes mention alerts
   
   Pages: 'project_detail' (Variations tab has the comment modal)
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const variationCommentsTour: TourDefinition = {
  id: 'variation_comments_v2_5',
  version: '2.5.0',
  type: 'feature_update',
  audience: 'all',
  page: 'project_detail',
  priority: 5,
  title: '@Mentions & Comments',
  subtitle: 'Tag team members in variation comments and project comms.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: '@Mentions Are Here',
      description: 'You can now tag team members directly in variation comments and project comms. Tagged members get notified in Microsoft Teams automatically.',
      icon: '💬',
      actionLabel: 'Show Me',
    },

    // ── Step 1: @Mention in Comments ─────────────────────────────────
    {
      id: 'mention-comments',
      type: 'info_card',
      title: 'Tag Team Members',
      description: 'Open any variation\'s comment modal and type @ to see a dropdown of team members. Select a person to insert a mention chip, and they\'ll be notified via Microsoft Teams.',
      icon: '👥',
      actionLabel: 'Next',
    },

    // ── Step 2: Comment Flags ────────────────────────────────────────
    {
      id: 'comment-flags',
      type: 'info_card',
      title: 'Comment Flags',
      description: 'Each comment has a flag color:\n\n• 🟢 **Green**: Resolved / Agreement Made\n• 🟣 **Purple**: Discussion / Question\n• 🔴 **Red**: Urgent / Action Required\n\nFlags are visible at a glance and included in Teams notifications.',
      icon: '🚩',
      actionLabel: 'Next',
    },

    // ── Step 3: Notes Tab Mentions ───────────────────────────────────
    {
      id: 'notes-mentions',
      type: 'info_card',
      title: 'Comms Tab Too!',
      description: 'The same @mention system works in the Comms tab. Type @ to tag team members alongside variations, categories, and claim reports. Tagged members are notified automatically.',
      icon: '📝',
      actionLabel: 'Got It',
    },

    // ── Step 4: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'Collaboration Upgraded!',
      description: 'Start using @mentions in comments and comms. Your team will be notified via Microsoft Teams whenever they\'re tagged.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
    },
  ],
};
